import { useChartStore } from '@/store/chartStore';
import { useMacroStore } from '@/store/macroStore';
import type { Chart } from 'klinecharts';
import { DomPosition } from 'klinecharts';
import { COMPARE_COLORS_EXPORT } from '@/components/Header/CompareBar';
import { INDICATORS } from '@/components/Chart/indicatorDefs';

// klinecharts default figure line colours (same order the library uses internally)
const IND_LINE_COLORS = ['#FF9600', '#935EBD', '#2196F3', '#E11D74', '#01C5C4'];

type ChartFull = Chart & {
  getConvertPictureUrl: (overlay: boolean, type: string, bg: string) => string;
  getSize: (paneId: string, position: number) => { top: number; left: number; width: number; height: number } | null;
};

function resolveVar(name: string, fallback: string): string {
  const val = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return val || fallback;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/** Draw a row of inline text segments, advancing x automatically. Returns final x. */
function drawSegments(
  ctx: CanvasRenderingContext2D,
  segments: Array<{ text: string; color: string; font: string }>,
  x0: number,
  y: number,
  gap = 4,
): number {
  let x = x0;
  for (const seg of segments) {
    ctx.font = seg.font;
    ctx.fillStyle = seg.color;
    ctx.fillText(seg.text, x, y);
    x += ctx.measureText(seg.text).width + gap;
  }
  return x;
}

function fmtVol(n: number): string {
  if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(2) + 'B';
  if (n >= 1_000_000)     return (n / 1_000_000).toFixed(2) + 'M';
  if (n >= 1_000)         return (n / 1_000).toFixed(1) + 'K';
  return n.toFixed(0);
}

/** Draw a rounded rectangle path (does not fill/stroke). */
function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/** Draw the fundamentals floating widget onto the canvas. */
function drawFundamentalsWidget(ctx: CanvasRenderingContext2D, widgetEl: HTMLElement, x: number, y: number) {
  const panel = widgetEl.querySelector<HTMLElement>('[data-fundamentals-panel]');
  if (!panel) return;
  const table = panel.querySelector('table');
  if (!table) return;

  const title = widgetEl.querySelector('[data-fundamentals-header-title]')?.textContent ?? '';
  const subtitle = widgetEl.querySelector('[data-fundamentals-header-subtitle]')?.textContent ?? '';
  const headers = Array.from(table.querySelectorAll('thead th')).map((th) => th.textContent ?? '');
  const rows = Array.from(table.querySelectorAll('tbody tr'))
    .filter((tr) => tr.querySelector('td'))
    .map((tr) => Array.from(tr.querySelectorAll('td')));

  const WIDGET_W = 336;
  const PAD = 8;
  const HEADER_H = 32;
  const ROW_H = 16;
  const totalH = HEADER_H + ROW_H + rows.length * ROW_H + 18 + PAD;

  const panelBg = resolveVar('--color-panel', '#1e222d');
  const borderC = resolveVar('--color-border', '#363a45');
  const textC = resolveVar('--color-text', '#d1d4dc');
  const mutedC = resolveVar('--color-muted', '#787b86');
  const accentC = resolveVar('--color-accent', '#2962ff');

  ctx.save();

  // Background
  ctx.fillStyle = panelBg;
  ctx.strokeStyle = borderC;
  ctx.lineWidth = 1;
  roundRectPath(ctx, x, y, WIDGET_W, totalH, 6);
  ctx.fill();
  ctx.stroke();

  // Header
  ctx.fillStyle = textC;
  ctx.font = `600 11px 'Helvetica Neue', Helvetica, Arial, sans-serif`;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText(title, x + PAD, y + 12);
  ctx.fillStyle = mutedC;
  ctx.font = `9px 'Helvetica Neue', Helvetica, Arial, sans-serif`;
  ctx.fillText(subtitle, x + PAD, y + 26);

  // Column headers
  let cy = y + HEADER_H;
  ctx.fillStyle = mutedC;
  ctx.font = `9px 'Helvetica Neue', Helvetica, Arial, sans-serif`;
  const colW = [72, 56, 56, 68, 68];
  const colX: number[] = [];
  let acc = 0;
  for (const w of colW) { acc += w; colX.push(acc); }
  for (let i = 0; i < headers.length && i < colX.length; i++) {
    ctx.textAlign = i === 0 ? 'left' : 'right';
    ctx.fillText(headers[i], x + (i === 0 ? PAD : colX[i] + PAD - 4), cy + ROW_H / 2 + 2);
  }

  // Divider under headers
  cy += ROW_H;
  ctx.strokeStyle = borderC;
  ctx.beginPath();
  ctx.moveTo(x + PAD, cy);
  ctx.lineTo(x + WIDGET_W - PAD, cy);
  ctx.stroke();

  // Rows
  for (let ri = 0; ri < rows.length; ri++) {
    const row = rows[ri];
    const ry = cy + ri * ROW_H;

    // Highlight first quarterly row
    if (ri === 0) {
      ctx.fillStyle = accentC + '0D';
      ctx.fillRect(x + 1, ry, WIDGET_W - 2, ROW_H);
    }

    for (let ci = 0; ci < row.length && ci < colX.length; ci++) {
      const cell = row[ci];
      const style = window.getComputedStyle(cell);
      ctx.fillStyle = style.color || textC;
      const fw = style.fontWeight === '600' || style.fontWeight === '700' || parseInt(style.fontWeight, 10) >= 600 ? '600' : '400';
      const italic = style.fontStyle === 'italic' ? 'italic ' : '';
      ctx.font = `${italic}${fw} 10px 'Helvetica Neue', Helvetica, Arial, sans-serif`;
      ctx.textAlign = ci === 0 ? 'left' : 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(cell.textContent ?? '', x + (ci === 0 ? PAD : colX[ci] + PAD - 4), ry + ROW_H / 2 + 1);
    }

    // Light row divider
    ctx.strokeStyle = borderC + '66';
    ctx.beginPath();
    ctx.moveTo(x + PAD, ry + ROW_H);
    ctx.lineTo(x + WIDGET_W - PAD, ry + ROW_H);
    ctx.stroke();
  }

  // Bottom legend line
  const ly = cy + rows.length * ROW_H + 4;
  ctx.strokeStyle = borderC + '66';
  ctx.beginPath();
  ctx.moveTo(x + PAD, ly);
  ctx.lineTo(x + WIDGET_W - PAD, ly);
  ctx.stroke();

  ctx.fillStyle = mutedC;
  ctx.font = `9px 'Helvetica Neue', Helvetica, Arial, sans-serif`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('≥25% · 1–24% · <0% · AE: Avg Est. · GE: Growth Est.', x + PAD, ly + 10);

  ctx.restore();
}

/**
 * Renders the chart with all React overlays (watermark, OHLCV legend, indicator
 * tooltips, compare rows, sub-pane labels, compare pills, fundamentals widget) composited onto the chart
 * image, plus a footer strip.
 */
async function renderChartCanvas(
  chart: Chart,
  wrapperEl: HTMLElement,
): Promise<{ canvas: HTMLCanvasElement; symbol: string; timeframe: string }> {
  const c = chart as ChartFull;
  const {
    symbol, watchlist, crosshair, lastBar, compareSymbols,
    compareAnchorTs, indicatorValues, indicatorPaneMap,
    theme, timeframe, rsLineConfigs,
  } = useChartStore.getState();

  const isDark     = theme === 'dark';
  const bg         = isDark ? '#131722' : '#ffffff';
  const colorText  = resolveVar('--color-text',  isDark ? '#d1d4dc' : '#131722');
  const colorMuted = resolveVar('--color-muted', '#787b86');
  const colorUp    = resolveVar('--color-up',    '#26a69a');
  const colorDown  = resolveVar('--color-down',  '#ef5350');

  const symbolName = watchlist.find((w) => w.symbol === symbol)?.name ?? '';
  const bar        = crosshair ?? lastBar;
  const open       = bar?.open  ?? null;
  const high       = bar?.high  ?? null;
  const low        = bar?.low   ?? null;
  const close      = bar?.close ?? 0;
  const volume     = bar?.volume ?? null;
  const isUp       = open == null || close >= open;
  const barColor   = isUp ? colorUp : colorDown;
  const change     = open != null ? close - open : null;
  const changePct  = change != null && open ? (change / open) * 100 : null;

  const fmt  = (n: number | null) => n == null ? '—' : n.toFixed(2);

  const W       = wrapperEl.clientWidth;
  const H       = wrapperEl.clientHeight;
  const FOOTER  = 36;

  // ── Pre-capture compare pill positions (DOM overlays) ───────────────────────
  const wrapperRect = wrapperEl.getBoundingClientRect();
  const pillsData = Array.from(wrapperEl.querySelectorAll<HTMLElement>('[data-compare-pill]'))
    .filter((pill) => pill.style.display !== 'none')
    .map((pill) => {
      const rect = pill.getBoundingClientRect();
      return {
        left: rect.left - wrapperRect.left,
        top: rect.top - wrapperRect.top,
        width: rect.width,
        height: rect.height,
        bg: pill.style.backgroundColor || '#2196F3',
        text: pill.textContent ?? '',
      };
    });

  // ── Base chart image at native DPR resolution ───────────────────────────────
  // KLineCharts renders its canvas at W×DPR × H×DPR. We simply capture that
  // natively and composite overlays at the same scale. No resize needed.
  const chartUrl = c.getConvertPictureUrl(true, 'png', bg);

  const img = await loadImage(chartUrl);
  const actualScale = img.naturalWidth / W;

  const canvas   = document.createElement('canvas');
  canvas.width   = Math.round(W * actualScale);
  canvas.height  = Math.round((H + FOOTER) * actualScale);
  const ctx      = canvas.getContext('2d')!;
  ctx.scale(actualScale, actualScale);

  // Draw chart image at CSS size — canvas scaling maps to physical 1:1
  ctx.drawImage(img, 0, 0, W, H);

  // ── Watermark ─────────────────────────────────────────────────────────────
  {
    const alpha    = isDark ? 0.10 : 0.10;
    const wmColor  = isDark ? `rgba(255,255,255,${alpha})` : `rgba(0,0,0,${alpha})`;
    const ff       = "'Helvetica Neue', Helvetica, Arial, sans-serif";
    const GAP      = 6;
    const lines: Array<{ text: string; size: number; weight: string }> = [
      { text: symbol,              size: 60, weight: '700' },
      ...(symbolName ? [{ text: symbolName, size: 24, weight: '400' }] : []),
      { text: 'CeylonCharts.com',  size: 20, weight: '400' },
    ];
    const totalH = lines.reduce((s, l) => s + l.size, 0) + GAP * (lines.length - 1);

    // Center watermark inside the candle pane only, not across sub-panes
    const candleSz = c.getSize('candle_pane', DomPosition.Root);
    const paneTop  = candleSz?.top ?? 0;
    const paneH    = candleSz?.height ?? H;
    let y = paneTop + (paneH - totalH) / 2;

    ctx.save();
    ctx.textAlign   = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle   = wmColor;
    for (const line of lines) {
      ctx.font = `${line.weight} ${line.size}px ${ff}`;
      ctx.fillText(line.text, W / 2, y);
      y += line.size + GAP;
    }
    ctx.restore();
  }

  // ── Legend (OHLCV + indicators + compare) at top-left ─────────────────────
  if (bar) {
    const PAD_X  = 10;
    const PAD_Y  = 6;
    const LINE_H = 20;
    const ff     = "'Helvetica Neue', Helvetica, Arial, sans-serif";
    const ffm    = 'monospace';

    ctx.save();
    ctx.textBaseline = 'top';
    let y = PAD_Y;

    // OHLCV row — company name only (or symbol if name unavailable)
    const ohlcvSegs: Array<{ text: string; color: string; font: string }> = [];
    ohlcvSegs.push({ text: symbolName || symbol, color: colorText, font: `600 14px ${ff}` });
    ohlcvSegs.push({ text: timeframe, color: colorMuted, font: `13px ${ff}` });
    ohlcvSegs.push({ text: '·',       color: colorMuted, font: `13px ${ff}` });

    // O H L C — muted prefix + coloured value as paired segments
    for (const [label, val] of [['O', fmt(open)], ['H', fmt(high)], ['L', fmt(low)], ['C', fmt(close)]]) {
      ohlcvSegs.push({ text: label, color: colorMuted, font: `13px ${ffm}` });
      ohlcvSegs.push({ text: ` ${val}`, color: barColor, font: `13px ${ffm}` });
    }
    if (volume != null && volume > 0) {
      ohlcvSegs.push({ text: 'V', color: colorMuted, font: `13px ${ffm}` });
      ohlcvSegs.push({ text: ` ${fmtVol(volume)}`, color: colorMuted, font: `13px ${ffm}` });
    }
    if (change != null && changePct != null) {
      const chStr  = (change >= 0 ? '+' : '') + change.toFixed(2);
      const pctStr = (changePct >= 0 ? '+' : '') + changePct.toFixed(2) + '%';
      ohlcvSegs.push({ text: `${chStr} (${pctStr})`, color: barColor, font: `13px ${ffm}` });
    }
    drawSegments(ctx, ohlcvSegs, PAD_X, y, 2);
    y += LINE_H;

    // Main-pane indicator rows (MA, EMA, BOLL, SAR)
    const mainPaneInds = INDICATORS.filter(
      (d) => indicatorPaneMap.has(d.name) && d.paneId === 'candle_pane',
    );
    for (const def of mainPaneInds) {
      const vals = indicatorValues[def.name];
      const segs: Array<{ text: string; color: string; font: string }> = [
        { text: def.name + (vals?.params ?? ''), color: colorMuted, font: `13px ${ffm}` },
      ];
      if (vals) {
        vals.entries.forEach((entry, fi) => {
          const lineColor = IND_LINE_COLORS[fi % IND_LINE_COLORS.length];
          segs.push({ text: `  ${entry.title}${entry.value}`, color: lineColor, font: `13px ${ffm}` });
        });
      }
      drawSegments(ctx, segs, PAD_X, y, 2);
      y += LINE_H;
    }

    // Custom candle-pane overlay (EPS_LINE)
    const customOverlayNames = ['EPS_LINE'];
    for (const name of customOverlayNames) {
      if (!indicatorPaneMap.has(name)) continue;
      const vals = indicatorValues[name];
      const segs: Array<{ text: string; color: string; font: string }> = [
        { text: name.replace(/_/g, ' '), color: colorMuted, font: `13px ${ffm}` },
      ];
      if (vals) {
        vals.entries.forEach((entry) => {
          segs.push({ text: `  ${entry.title}${entry.value}`, color: '#2196F3', font: `13px ${ffm}` });
        });
      }
      drawSegments(ctx, segs, PAD_X, y, 2);
      y += LINE_H;
    }

    // Compare row — symbol chip + price + %
    const compareTime = crosshair?.time ?? lastBar?.time ?? 0;
    if (compareSymbols.length > 0) {
      let x = PAD_X;
      for (let i = 0; i < compareSymbols.length; i++) {
        const sym       = compareSymbols[i];
        const chipColor = COMPARE_COLORS_EXPORT[i % COMPARE_COLORS_EXPORT.length];
        const map       = (window as unknown as Record<string, Map<number, number> | undefined>)[`__compare_COMPARE_${i}`];
        const price     = compareTime > 0 ? (map?.get(compareTime) ?? null) : null;
        const anchorPx  = compareAnchorTs > 0 ? (map?.get(compareAnchorTs) ?? null) : null;
        const pct       = price != null && anchorPx != null && anchorPx !== 0
          ? (price - anchorPx) / anchorPx * 100
          : null;

        ctx.font      = `600 13px ${ffm}`;
        ctx.fillStyle = chipColor;
        ctx.fillText(sym, x, y);
        x += ctx.measureText(sym).width + 2;

        if (price != null) {
          const pctStr = pct != null ? ` (${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%)` : '';
          ctx.font      = `13px ${ffm}`;
          ctx.fillStyle = chipColor;
          ctx.fillText(price.toFixed(2) + pctStr, x, y);
          x += ctx.measureText(price.toFixed(2) + pctStr).width + 10;
        } else {
          x += 10;
        }
      }
      y += LINE_H;
    }

    // RS Line legend row — all configs inline (matches viewport flex layout)
    if (indicatorPaneMap.has('RS_LINE') && rsLineConfigs.length > 0) {
      const rsLines = (window as unknown as { __rs_lines?: Array<{ id: string; label: string; rsRating: number; color: string; visible: boolean }> }).__rs_lines ?? [];
      let x = PAD_X;
      for (const cfg of rsLineConfigs) {
        const renderCfg = rsLines.find((r) => r.id === cfg.id);
        const rating = renderCfg?.rsRating ?? 0;
        const label = renderCfg?.label ?? cfg.benchmarkSymbol;
        const dimmed = !cfg.visible;
        const lineColor = cfg.color;

        // Color swatch (line)
        ctx.globalAlpha = dimmed ? 0.4 : 1;
        ctx.fillStyle = dimmed ? 'transparent' : lineColor;
        ctx.fillRect(x, y + 6, 18, 2);
        if (dimmed) {
          ctx.strokeStyle = lineColor;
          ctx.lineWidth = 1;
          ctx.setLineDash([2, 2]);
          ctx.strokeRect(x, y + 6, 18, 2);
          ctx.setLineDash([]);
        }
        x += 24;

        // Label
        ctx.font = `600 13px ${ffm}`;
        ctx.fillStyle = lineColor;
        ctx.fillText(label, x, y);
        x += ctx.measureText(label).width + 6;

        // RS Rating badge
        if (!dimmed) {
          const badgeText = `RS ${rating > 0 ? rating : '—'}`;
          ctx.font = `700 11px ${ffm}`;
          const badgeW = ctx.measureText(badgeText).width + 8;
          const badgeH = 14;
          const badgeY = y + 3;

          // Badge background
          ctx.fillStyle = lineColor + '18'; // 10% opacity
          ctx.strokeStyle = lineColor;
          ctx.lineWidth = 1;
          roundRectPath(ctx, x, badgeY, badgeW, badgeH, 3);
          ctx.fill();
          ctx.stroke();

          // Badge text
          ctx.fillStyle = lineColor;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(badgeText, x + badgeW / 2, badgeY + badgeH / 2);
          ctx.textAlign = 'left';
          ctx.textBaseline = 'top';
          x += badgeW + 12;
        } else {
          x += 6;
        }
      }
      ctx.globalAlpha = 1;
      y += LINE_H;
    }

    ctx.restore();

    // ── Sub-pane labels — positioned at top of each indicator pane ────────────
    ctx.save();
    ctx.textBaseline = 'top';

    // VOL pane
    if (indicatorPaneMap.has('VOL')) {
      const sz = c.getSize('vol_pane', DomPosition.Root);
      if (sz) {
        const vals = indicatorValues['VOL'];
        const segs: Array<{ text: string; color: string; font: string }> = [
          { text: 'VOL' + (vals?.params ?? ''), color: colorMuted, font: `13px ${ffm}` },
        ];
        if (vals) {
          vals.entries.forEach((entry, fi) => {
            const isRawVol  = fi === 0;
            const lineColor = isRawVol ? barColor : IND_LINE_COLORS[(fi - 1) % IND_LINE_COLORS.length];
            const num       = parseFloat(entry.value);
            const display   = !isNaN(num) ? fmtVol(num) : entry.value;
            segs.push({ text: `  ${entry.title}${display}`, color: lineColor, font: `13px ${ffm}` });
          });
        }
        drawSegments(ctx, segs, PAD_X, sz.top + PAD_Y, 2);
      }
    }

    // User sub-pane indicators (MACD, RSI, KDJ, etc.)
    const userSubPaneInds = ([...indicatorPaneMap.keys()] as string[])
      .filter((name) => {
        const def = INDICATORS.find((i) => i.name === name);
        return def && !def.paneId;
      })
      .map((name) => INDICATORS.find((i) => i.name === name)!);

    for (const def of userSubPaneInds) {
      const paneId = indicatorPaneMap.get(def.name);
      if (!paneId) continue;
      const sz = c.getSize(paneId, DomPosition.Root);
      if (!sz) continue;
      const vals = indicatorValues[def.name];
      const segs: Array<{ text: string; color: string; font: string }> = [
        { text: def.name + (vals?.params ?? ''), color: colorMuted, font: `13px ${ffm}` },
      ];
      if (vals) {
        vals.entries.forEach((entry, fi) => {
          const lineColor = IND_LINE_COLORS[fi % IND_LINE_COLORS.length];
          segs.push({ text: `  ${entry.title}${entry.value}`, color: lineColor, font: `13px ${ffm}` });
        });
      }
      drawSegments(ctx, segs, PAD_X, sz.top + PAD_Y, 2);
    }

    // Macro indicators
    const macroPrefix = 'MACRO_';
    for (const [indName, paneId] of indicatorPaneMap) {
      if (!indName.startsWith(macroPrefix)) continue;
      const sz = c.getSize(paneId, DomPosition.Root);
      if (!sz) continue;
      const vals = indicatorValues[indName];
      const seriesId = indName.slice(macroPrefix.length);
      const pinned = useMacroStore.getState().pinnedSeries.find((s) => s.seriesId === seriesId);
      const meta = useMacroStore.getState().seriesMeta.find((m) => m.series_id === seriesId);
      const displayName = meta?.name ?? seriesId;
      const lineColor = pinned?.color ?? '#3b82f6';

      const segs: Array<{ text: string; color: string; font: string }> = [
        { text: displayName, color: lineColor, font: `13px ${ffm}` },
      ];
      if (vals) {
        vals.entries.forEach((entry) => {
          segs.push({ text: `  ${entry.title}${entry.value}`, color: lineColor, font: `13px ${ffm}` });
        });
      }
      drawSegments(ctx, segs, PAD_X, sz.top + PAD_Y, 2);
    }

    ctx.restore();
  }

  // ── Compare pills (right Y-axis coloured % labels) ─────────────────────────
  for (const pill of pillsData) {
    ctx.save();
    const r = 2;
    ctx.fillStyle = pill.bg;
    ctx.beginPath();
    ctx.moveTo(pill.left + r, pill.top);
    ctx.lineTo(pill.left + pill.width - r, pill.top);
    ctx.quadraticCurveTo(pill.left + pill.width, pill.top, pill.left + pill.width, pill.top + r);
    ctx.lineTo(pill.left + pill.width, pill.top + pill.height - r);
    ctx.quadraticCurveTo(pill.left + pill.width, pill.top + pill.height, pill.left + pill.width - r, pill.top + pill.height);
    ctx.lineTo(pill.left + r, pill.top + pill.height);
    ctx.quadraticCurveTo(pill.left, pill.top + pill.height, pill.left, pill.top + pill.height - r);
    ctx.lineTo(pill.left, pill.top + r);
    ctx.quadraticCurveTo(pill.left, pill.top, pill.left + r, pill.top);
    ctx.closePath();
    ctx.fill();

    ctx.font         = "11px 'Helvetica Neue', Helvetica, Arial, sans-serif";
    ctx.fillStyle    = '#ffffff';
    ctx.textBaseline = 'middle';
    ctx.textAlign    = 'center';
    ctx.fillText(pill.text, pill.left + pill.width / 2, pill.top + pill.height / 2);
    ctx.restore();
  }

  // ── Fundamentals floating widget ──────────────────────────────────────────
  {
    const widgetEl = document.querySelector<HTMLElement>('[data-fundamentals-widget]');
    if (widgetEl) {
      const wrapperRect = wrapperEl.getBoundingClientRect();
      const widgetRect = widgetEl.getBoundingClientRect();
      const wx = widgetRect.left - wrapperRect.left;
      const wy = widgetRect.top - wrapperRect.top;
      drawFundamentalsWidget(ctx, widgetEl, wx, wy);
    }
  }

  // ── Footer strip ──────────────────────────────────────────────────────────
  {
    const footerBg   = isDark ? '#0d1117' : '#f0f3fa';
    const footerText = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
    const dateStr    = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    const ff         = "'Helvetica Neue', Helvetica, Arial, sans-serif";

    ctx.fillStyle = footerBg;
    ctx.fillRect(0, H, W, FOOTER);

    ctx.font         = `12px ${ff}`;
    ctx.textBaseline = 'middle';

    ctx.fillStyle = footerText;
    ctx.textAlign = 'left';
    ctx.fillText(`${symbol} · ${timeframe} · ${dateStr}`, 16, H + FOOTER / 2);

    ctx.fillStyle = footerText;
    ctx.textAlign = 'right';
    ctx.fillText('Generated by CeylonCharts.com', W - 16, H + FOOTER / 2);
  }

  return { canvas, symbol, timeframe };
}

/** Renders the chart and triggers a JPEG download. */
export async function captureChartScreenshot(chart: Chart, wrapperEl: HTMLElement): Promise<void> {
  const { canvas, symbol, timeframe } = await renderChartCanvas(chart, wrapperEl);
  const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
  const a       = document.createElement('a');
  a.download    = `${symbol}-${timeframe}.jpg`;
  a.href        = dataUrl;
  a.click();
}

// Link-preview cards (WhatsApp, Slack, X, …) crop to ~1.91:1, so the whole chart is
// fitted inside a fixed 1200×630 frame rather than cropped — nothing gets cut off.
const PREVIEW_W = 1200;
const PREVIEW_H = 630;
const PREVIEW_MAX_BYTES = 400 * 1024; // must match the server's limit in functions/api/share/index.ts

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Renders the chart as a 1200×630 JPEG for a shared link's preview card and
 * returns it base64-encoded (no data: prefix), or null if it couldn't be made.
 */
export async function captureChartPreview(chart: Chart, wrapperEl: HTMLElement): Promise<string | null> {
  try {
    const { canvas } = await renderChartCanvas(chart, wrapperEl);

    const frame = document.createElement('canvas');
    frame.width  = PREVIEW_W;
    frame.height = PREVIEW_H;
    const ctx = frame.getContext('2d');
    if (!ctx) return null;

    ctx.fillStyle = useChartStore.getState().theme === 'dark' ? '#131722' : '#ffffff';
    ctx.fillRect(0, 0, PREVIEW_W, PREVIEW_H);

    const scale = Math.min(PREVIEW_W / canvas.width, PREVIEW_H / canvas.height);
    const w = canvas.width * scale;
    const h = canvas.height * scale;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(canvas, (PREVIEW_W - w) / 2, (PREVIEW_H - h) / 2, w, h);

    for (const quality of [0.85, 0.7, 0.55]) {
      const blob = await new Promise<Blob | null>((resolve) => frame.toBlob(resolve, 'image/jpeg', quality));
      if (blob && blob.size <= PREVIEW_MAX_BYTES) return await blobToBase64(blob);
    }
    return null;
  } catch {
    return null;
  }
}
